/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormHelperText,
  CircularProgress,
  Alert,
  FormControlLabel,
  Box
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { httpRequest } from 'src/services/httpRequest';

import {
  BidFormData,
  BidFormErrors,
  TypeOption,
  CategoryOption,
  DepartmentOption,
  StatusOption
} from './index';
import { DatePicker } from '@mui/x-date-pickers';
import { addCommaWithTwoDecimals } from 'src/services/common';

// --- NEW: extend data model with Re-Open/Award override fields
interface ExtendedBidFormData extends BidFormData {
  awardedToVendor?: string;
  awardedToContact?: string;
  awardedToContactId: number;
  bidResult?: File | null;
  bidResultUrl?: string | null;
  bidResultTitle?: string;
  bidResultUploaded?: boolean;
  bidResultError?: string | null;
  bidResultIsUploading?: boolean;
  poFile?: File | null;
  poFileUrl?: string | null;
  poFileTitle?: string;
  poFileUploaded?: boolean;
  poFileError?: string | null;
  poFileIsUploading?: boolean;
  poNumber: string;
  awardAmount?: string;
  isAddendum: boolean;

  // NEW ↓
  reopenAward?: boolean; // checkbox state
  reopenReason?: string; // required when reopenAward is true
}

interface ExtendedBidFormErrors extends BidFormErrors {
  awardedToVendor?: string;
  awardedToContact?: string;
  bidResult?: string;
  poNumber?: string;
  poFile?: string;
  awardDate?: string;
  // NEW ↓
  reopenReason?: string;
}

interface BidDetailsFormProps {
  formData: ExtendedBidFormData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedBidFormData>>;
  errors: ExtendedBidFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<ExtendedBidFormErrors>>;
  types: TypeOption[];
  categories: CategoryOption[];
  departments: DepartmentOption[];
  statuses: StatusOption[];
  isGeneratingContractNo: boolean;
  onBidTypeChange: (selectedType: string) => Promise<void>;
  handleFileUploadForPOAndBidResult: (
    index: number,
    file: File
  ) => Promise<{ fileUrl: string; fileName: string; fileType: string }>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  isFormDisabled: any;
  originalStatus: string;
}

const BidDetailsForm: React.FC<BidDetailsFormProps> = ({
  formData,
  setFormData,
  errors,
  setErrors,
  types,
  categories,
  departments,
  statuses,
  isGeneratingContractNo,
  onBidTypeChange,
  // handleFileUploadForPOAndBidResult,
  // setError,
  isFormDisabled,
  originalStatus
}) => {
  const { bidId } = useParams<{ bidId: string }>();

  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [previousAdminNotes, setPreviousAdminNotes] = useState<string | null>(
    null
  );

  // Date without time
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentDateTime = new Date();
  const tomorrowDateTime = new Date();
  tomorrowDateTime.setDate(currentDateTime.getDate() + 1);
  tomorrowDateTime.setHours(0, 0, 0, 0);

  const [contacts, setContacts] = useState<
    { id: string; name: string; userId?: number; displayName: string }[]
  >([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  const filteredStatus = statuses?.filter((item) => {
    if (originalStatus === 'Open') {
      return item.Status !== 'Draft';
    } else {
      return true;
    }
  });

  const fetchedVendorsRef = useRef<Set<string>>(new Set());

  // --- NEW: override helper (when checkbox is checked, EVERYTHING is enabled)
  const overrideEnabled = !!formData.reopenAward;
  const isDisabled = overrideEnabled ? false : !!isFormDisabled;

  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    setVendorError(null);
    try {
      const response = await httpRequest(
        'GET',
        '/auth/vendors_information',
        {},
        {}
      );
      if (response.status === 'success' && response.data?.BusinessDetails) {
        const vendorsList = response.data.BusinessDetails.map(
          (vendor: { Business: string }, index: number) => ({
            id: `vendor-${index}`,
            name: vendor.Business
          })
        );
        setVendors(vendorsList);
      } else {
        console.error('Unexpected vendor response format:', response);
        setVendorError(
          response && response?.message
            ? response.message
            : 'Failed to load vendors. Unexpected response format.'
        );
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendorError('Failed to load vendors. Please try again.');
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  const fetchContacts = useCallback(
    async (vendorName: string) => {
      if (!vendorName || loadingContacts) return;

      setLoadingContacts(true);
      setContactError(null);
      setContacts([]);

      // ✅ mark vendor as fetched (IMPORTANT)
      fetchedVendorsRef.current.add(vendorName);

      try {
        const url = `/auth/vendors_information?Business=${encodeURIComponent(
          vendorName
        )}${bidId ? `&BidID=${bidId}` : ''}`;

        const response = await httpRequest('GET', url, {}, {});

        if (
          response.status === 'success' &&
          Array.isArray(response.data?.VendorDetails)
        ) {
          const contactsList = response.data.VendorDetails.map((contact: any) => ({
            id: `contact-${contact.UserID || contact.FullNameWithEmail}`,
            name:
              contact.FullNameWithEmail ||
              `${contact.FirstName} ${contact.LastName}`,
            userId: contact.UserID,
            displayName:
              contact.FullNameWithEmail ||
              `${contact.FirstName} ${contact.LastName}`
          }));

          setContacts(contactsList);

          // ✅ AUTO-SELECT ONLY IF DATA EXISTS
          if (contactsList.length > 0) {
            const firstContact = contactsList[0];
            setFormData((prev) => ({
              ...prev,
              awardedToContact: firstContact.name,
              awardedToContactId: firstContact.userId || 0
            }));
          } else {
            // ✅ NO DATA → KEEP BLANK
            setFormData((prev) => ({
              ...prev,
              awardedToContact: '',
              awardedToContactId: 0
            }));
          }
        } else {
          // No data case
          setContacts([]);
          setFormData((prev) => ({
            ...prev,
            awardedToContact: '',
            awardedToContactId: 0
          }));
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setContactError('Failed to load contacts. Please try again.');

        // allow retry on error only
        fetchedVendorsRef.current.delete(vendorName);
      } finally {
        setLoadingContacts(false);
      }
    },
    [bidId, loadingContacts, setFormData]
  );

  // const fetchContacts = useCallback(
  //   async (vendorName: string) => {
  //     if (!vendorName) return;
  //     // Remove this line if you want API call every time
  //     if (fetchedVendorsRef.current.has(vendorName)) return;

  //     if (loadingContacts) return;

  //     setLoadingContacts(true);
  //     setContactError(null);
  //     setContacts([]);

  //     try {
  //       // Optional: keep track if needed, but don't block API call
  //       // fetchedVendorsRef.current.add(vendorName);
  //       const url = `/auth/vendors_information?Business=${encodeURIComponent(
  //         vendorName
  //       )}${bidId ? `&BidID=${bidId}` : ''}`;
  //       const response = await httpRequest('GET', url, {}, {});
  //       if (response.status === 'success') {
  //         if (
  //           response.data?.VendorDetails &&
  //           Array.isArray(response.data.VendorDetails)
  //         ) {
  //           const contactsList = response.data.VendorDetails.map((contact: any) => ({
  //             id: `contact-${contact.UserID || contact.FullNameWithEmail}`,
  //             name: contact.FullNameWithEmail || `${contact.FirstName} ${contact.LastName}`,
  //             userId: contact.UserID,
  //             displayName: contact.FullNameWithEmail || `${contact.FirstName} ${contact.LastName}`
  //           }));
  //           setContacts(contactsList);
  //           if (contactsList.length > 0) {
  //             const firstContact = contactsList[0];
  //             setFormData((prevData) => ({
  //               ...prevData,
  //               awardedToContact: firstContact.name,
  //               awardedToContactId: firstContact.userId || 0
  //             }));
  //           }
  //         } else {
  //           setContacts([]);
  //         }
  //       } else {
  //         setContactError('Failed to load contacts. Please try again.');
  //       }
  //     } catch (error) {
  //       console.error('Error fetching contacts:', error);
  //       setContactError('Failed to load contacts. Please try again.');
  //       // Remove only on error if you want retry
  //       fetchedVendorsRef.current.delete(vendorName);
  //     } finally {
  //       setLoadingContacts(false);
  //     }
  //   },
  //   [bidId, loadingContacts, setFormData]
  // );

  const handleBidTypeChange = (e: SelectChangeEvent) => {
    onBidTypeChange(e.target.value);
    if (errors.bidType) setErrors((prev) => ({ ...prev, bidType: '' }));
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    const newStatus = e.target.value;
    setFormData({ ...formData, status: newStatus, isAddendum: false });
    if (errors.status) setErrors((prev) => ({ ...prev, status: '' }));

    if (newStatus === 'Awarded' && vendors.length === 0 && !loadingVendors) {
      fetchVendors();
    }
  };

  const handleAddendumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFormData({
      ...formData,
      isAddendum: checked,
      adminNotes: checked ? '' : previousAdminNotes || ''
    });
    if (checked) {
      setPreviousAdminNotes(formData.adminNotes || '');
    }
  };

  // --- NEW: handle Re-Open/Award checkbox
  const handleReopenAwardChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      reopenAward: checked,
      reopenReason: checked ? prev.reopenReason || '' : '',
      // 👇 change status when toggled
      status: checked ? 'Open' : originalStatus // or prev.status if you don't want to revert
    }));

    // clear any status/reopenReason errors if applicable
    setErrors((prev) => ({
      ...prev,
      status: '',
      ...(checked ? { reopenReason: '' } : {})
    }));
  };

  // const validateFile = (file: File): string | null => {
  //   const allowedExtensions = [
  //     '.pdf',
  //     '.xls',
  //     '.xlsx',
  //     '.doc',
  //     '.docx',
  //     '.zip'
  //   ];
  //   const fileExtension = file.name
  //     .substring(file.name.lastIndexOf('.'))
  //     .toLowerCase();
  //   if (!allowedExtensions.includes(fileExtension)) {
  //     return `Invalid file type. Only ${allowedExtensions.join(
  //       ', '
  //     )} files are allowed.`;
  //   }
  //   const maxSize = 10 * 1024 * 1024;
  //   if (file.size > maxSize) {
  //     return `File is too large. Maximum file size is 10MB.`;
  //   }
  //   return null;
  // };

  // const processFileUpload = async (
  //   fileType: 'bidResult' | 'poFile',
  //   file: File
  // ) => {
  //   const validationError = validateFile(file);
  //   if (validationError) {
  //     if (fileType === 'bidResult')
  //       setErrors((prev) => ({ ...prev, bidResult: validationError }));
  //     else setErrors((prev) => ({ ...prev, poFile: validationError }));
  //     setError(validationError);
  //     return;
  //   }

  //   if (fileType === 'bidResult') {
  //     setFormData({
  //       ...formData,
  //       bidResult: file,
  //       bidResultError: null,
  //       bidResultIsUploading: true
  //     });
  //     setErrors((prev) => ({ ...prev, bidResult: '' }));
  //   } else {
  //     setFormData({
  //       ...formData,
  //       poFile: file,
  //       poFileError: null,
  //       poFileIsUploading: true
  //     });
  //     setErrors((prev) => ({ ...prev, poFile: '' }));
  //   }

  //   try {
  //     const fileIndex = fileType === 'bidResult' ? 0 : 1;
  //     const fileResponse = await handleFileUploadForPOAndBidResult(
  //       fileIndex,
  //       file
  //     );
  //     if (fileResponse) {
  //       const { fileUrl, fileName } = fileResponse;
  //       if (fileType === 'bidResult') {
  //         setFormData({
  //           ...formData,
  //           bidResult: file,
  //           bidResultUrl: fileUrl,
  //           bidResultTitle: fileName,
  //           bidResultUploaded: true,
  //           bidResultError: null,
  //           bidResultIsUploading: false
  //         });
  //       } else {
  //         setFormData({
  //           ...formData,
  //           poFile: file,
  //           poFileUrl: fileUrl,
  //           poFileTitle: fileName,
  //           poFileUploaded: true,
  //           poFileError: null,
  //           poFileIsUploading: false
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error(`Error uploading ${fileType}:`, error);
  //     const errorMessage = 'Failed to upload file. Please try again.';
  //     if (fileType === 'bidResult') {
  //       setFormData({
  //         ...formData,
  //         bidResult: file,
  //         bidResultUploaded: false,
  //         bidResultError: errorMessage,
  //         bidResultIsUploading: false
  //       });
  //       setErrors((prev) => ({ ...prev, bidResult: errorMessage }));
  //     } else {
  //       setFormData({
  //         ...formData,
  //         poFile: file,
  //         poFileUploaded: false,
  //         poFileError: errorMessage,
  //         poFileIsUploading: false
  //       });
  //       setErrors((prev) => ({ ...prev, poFile: errorMessage }));
  //     }
  //     setError(errorMessage);
  //   }
  // };

  // const handleRemoveFile = (fileType: 'bidResult' | 'poFile') => {
  //   if (fileType === 'bidResult') {
  //     setFormData({
  //       ...formData,
  //       bidResult: null,
  //       bidResultUrl: null,
  //       bidResultTitle: '',
  //       bidResultUploaded: false,
  //       bidResultError: null
  //     });
  //     setErrors((prev) => ({ ...prev, bidResult: '' }));
  //   } else {
  //     setFormData({
  //       ...formData,
  //       poFile: null,
  //       poFileUrl: null,
  //       poFileTitle: '',
  //       poFileUploaded: false,
  //       poFileError: null
  //     });
  //     setErrors((prev) => ({ ...prev, poFile: '' }));
  //   }
  // };

  const isAwarded = formData.status === 'Awarded';
  const isClosed = formData.status === 'Close' || formData.status === 'Closed';
  const showAwardDetails = isAwarded || isClosed;
  const isOpen = formData.status === 'Open';

  // useEffect(() => {
  //   if (
  //     isAwarded &&
  //     formData.awardedToVendor &&
  //     !loadingContacts &&
  //     contacts.length === 0 &&
  //     !fetchedVendorsRef.current.has(formData.awardedToVendor)
  //   ) {
  //     fetchContacts(formData.awardedToVendor);
  //   }
  // }, [
  //   isAwarded,
  //   vendors.length,
  //   loadingVendors,
  //   fetchVendors,
  //   formData.awardedToVendor,
  //   loadingContacts,
  //   contacts.length,
  //   fetchContacts
  // ]);

  useEffect(() => {
    if (
      isAwarded &&
      formData.awardedToVendor &&
      !loadingContacts &&
      !fetchedVendorsRef.current.has(formData.awardedToVendor)
    ) {
      fetchContacts(formData.awardedToVendor);
    }
  }, [
    isAwarded,
    formData.awardedToVendor,
    loadingContacts,
    fetchContacts
  ]);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    return () => {
      fetchedVendorsRef.current.clear();
    };
  }, []);

  const handleVendorChange = (e: SelectChangeEvent) => {
    const selectedVendor = e.target.value;
    const currentVendor = formData.awardedToVendor;
    if (selectedVendor !== currentVendor) {
      setFormData({
        ...formData,
        awardedToVendor: selectedVendor,
        awardedToContact: ''
      });
      if (errors.awardedToVendor && selectedVendor)
        setErrors((prev) => ({ ...prev, awardedToVendor: '' }));
      if (errors.awardedToContact)
        setErrors((prev) => ({ ...prev, awardedToContact: '' }));
      if (selectedVendor && !loadingContacts) fetchContacts(selectedVendor);
    }
  };

  const handleContactChange = (e: SelectChangeEvent) => {
    const selectedContactName = e.target.value;
    const selectedContact = contacts.find(
      (contact) => contact.name === selectedContactName
    );
    setFormData({
      ...formData,
      awardedToContact: selectedContactName,
      awardedToContactId:
        selectedContact?.userId || formData.awardedToContactId || 0
    });
    if (errors.awardedToContact && selectedContactName) {
      setErrors((prev) => ({ ...prev, awardedToContact: '' }));
    }
  };

  // const isGoLiveDateValid = (date: Date | null): boolean => {
  //   if (!date) return true;
  //   return date >= tomorrowDateTime;
  // };

  // const isBidDeadlineValid = (date: Date | null): boolean => {
  //   if (!date) return true;
  //   if (!formData.goLiveDate) return date >= tomorrowDateTime;
  //   return date > formData.goLiveDate;
  // };

  // const isAwardDateValid = (date: Date | null): boolean => {
  //   if (!date) return true;
  //   if (!formData.bidDeadline) return date >= tomorrowDateTime;
  //   return date > formData.bidDeadline;
  // };

  // const getGoLiveDateError = (): string => {
  //   if (!formData.goLiveDate) return '';
  //   // when override is on, don't show disabled-only validation gating
  //   if (!overrideEnabled && (!isDraft || isFormDisabled)) return '';
  //   if (!isGoLiveDateValid(formData.goLiveDate))
  //     return 'Go Live Date must be greater than today';
  //   return '';
  // };

  // const getBidDeadlineError = (): string => {
  //   if (!formData.bidDeadline) return '';
  //   if (formData.goLiveDate && formData.bidDeadline <= formData.goLiveDate) {
  //     return 'Bid Deadline must be greater than Go Live Date';
  //   }
  //   if (!isBidDeadlineValid(formData.bidDeadline)) {
  //     return 'Bid Deadline must be greater than today';
  //   }
  //   return '';
  // };

  // const getAwardDateError = (): string => {
  //   if (!formData.awardDate) return '';
  //   if (formData.bidDeadline && formData.awardDate <= formData.bidDeadline) {
  //     return 'Award Date must be greater than Bid Deadline';
  //   }
  //   if (!isAwardDateValid(formData.awardDate)) {
  //     return 'Award Date must be greater than today';
  //   }
  //   return '';
  // };

  const getMinDate = (fieldDate: Date | null): Date => {
    if (fieldDate) {
      const minDate = new Date(fieldDate);
      minDate.setHours(0, 0, 0, 0);
      return minDate;
    }
    return today;
  };

  return (
    <Grid container spacing={3}>
      {isFormDisabled && formData.status === 'Awarded' && !overrideEnabled && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 2 }}>
            As this bid is awarded to vendor so no addendum can be done now.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.bidType} required>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.bidType || ''}
            onChange={handleBidTypeChange}
            label="Type"
            required
            disabled
          >
            {types.map((type) => (
              <MenuItem key={type.TypeID} value={type.Type}>
                {type.Type}
              </MenuItem>
            ))}
          </Select>
          {errors.bidType && <FormHelperText>{errors.bidType}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contract No."
          value={formData.contractNo || ''}
          onChange={(e) => {
            setFormData({ ...formData, contractNo: e.target.value });
            if (errors.contractNo)
              setErrors((prev) => ({ ...prev, contractNo: '' }));
          }}
          error={!!errors.contractNo}
          helperText={errors.contractNo}
          InputProps={{
            endAdornment: isGeneratingContractNo ? (
              <CircularProgress size={20} />
            ) : null,
            readOnly: isGeneratingContractNo
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={formData.title || ''}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            if (errors.title && e.target.value.trim() !== '')
              setErrors((prev) => ({ ...prev, title: '' }));
          }}
          error={!!errors.title}
          helperText={errors.title}
          required
          disabled={isDisabled}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth error={!!errors.categories} required>
          <InputLabel>Categories</InputLabel>
          <Select
            open={openCategorySelect}
            onOpen={() => setOpenCategorySelect(true)}
            onClose={() => setOpenCategorySelect(false)}
            disabled={isDisabled}
            multiple
            value={formData.categories || []}
            onChange={(e) => {
              const newCategories =
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : (e.target.value as string[]);
              if (newCategories.includes('SELECT_ALL')) {
                const allCategoryNames = categories.map(
                  (cat) => cat.CategoryName
                );
                const currentCategories = formData.categories || [];
                const isAllSelected =
                  currentCategories.length === categories.length;
                const finalCategories = isAllSelected ? [] : allCategoryNames;
                setFormData({ ...formData, categories: finalCategories });
                if (errors.categories && finalCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              } else {
                setFormData({ ...formData, categories: newCategories });
                if (errors.categories && newCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 350,
                  borderRadius: 1.5,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  marginTop: 1
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            input={<OutlinedInput label="Categories" />}
            renderValue={(selected) => (selected as string[]).join(', ')}
            required
          >
            <MenuItem value="SELECT_ALL">
              <Checkbox
                checked={
                  (formData.categories || []).length === categories.length
                }
                indeterminate={
                  (formData.categories || []).length > 0 &&
                  (formData.categories || []).length < categories.length
                }
              />
              <ListItemText
                primary="Select All"
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              />
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.CategoryID} value={category.CategoryName}>
                <Checkbox
                  checked={
                    (formData.categories || []).indexOf(category.CategoryName) >
                    -1
                  }
                />
                <ListItemText primary={category.CategoryName} />
              </MenuItem>
            ))}
            {/* ✅ Footer Button to Close Dropdown */}

            <MenuItem
              disableRipple
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #ddd',
                mt: 1,
                pt: 1
              }}
              onClick={(e) => e.stopPropagation()} // ✅ Prevent MenuItem click
            >
              <button
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ Prevent event bubbling
                  setOpenCategorySelect(false);
                }}
              >
                Apply
              </button>
            </MenuItem>
          </Select>
          {errors.categories && (
            <FormHelperText>{errors.categories}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.department} required>
          <InputLabel>Department</InputLabel>
          <Select
            disabled={isDisabled}
            value={formData.department || ''}
            onChange={(e: SelectChangeEvent) => {
              setFormData({ ...formData, department: e.target.value });
              if (errors.department && e.target.value)
                setErrors((prev) => ({ ...prev, department: '' }));
            }}
            label="Department"
            required
          >
            {departments.map((dept) => (
              <MenuItem key={dept.DepartmentID} value={dept.DepartmentName}>
                {dept.DepartmentName}
              </MenuItem>
            ))}
          </Select>
          {errors.department && (
            <FormHelperText>{errors.department}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.status} required>
          <InputLabel>Status</InputLabel>
          <Select
            disabled={isDisabled}
            value={formData.status || ''}
            onChange={handleStatusChange}
            label="Status"
            required
          >
            {filteredStatus?.map((status) => (
              <MenuItem key={status.StatusID} value={status.Status}>
                {status.Status}
              </MenuItem>
            ))}
          </Select>
          {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
        </FormControl>

        {/* Checkbox sits right under Status */}
        {(originalStatus === 'Close' || originalStatus === 'Closed') && (
          <Box mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.reopenAward}
                  onChange={handleReopenAwardChange}
                  size="small"
                />
              }
              label="Unclose (Re-Open) / Award"
            />
          </Box>
        )}
      </Grid>

      {/* Addendum Checkbox (unchanged) */}
      {isOpen && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isAddendum || false}
                onChange={handleAddendumChange}
                disabled={isDisabled}
              />
            }
            label="Check if any addendum in bid"
          />
        </Grid>
      )}

      {/* Award / Close sections... */}
      {showAwardDetails && (
        <>
          {isAwarded && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.awardedToVendor}>
                  <InputLabel>Awarded To (Vendor)</InputLabel>
                  <Select
                    value={formData.awardedToVendor || ''}
                    onChange={handleVendorChange}
                    label="Awarded To (Vendor)"
                    disabled={loadingVendors || isDisabled}
                    startAdornment={
                      loadingVendors ? (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      ) : null
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      }
                    }}
                  >
                    {formData.awardedToVendor &&
                      !vendors.some(
                        (v) => v.name === formData.awardedToVendor
                      ) && (
                        <MenuItem value={formData.awardedToVendor}>
                          {formData.awardedToVendor} (Previously Selected)
                        </MenuItem>
                      )}
                    {vendors.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.name}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.awardedToVendor && (
                    <FormHelperText>{errors.awardedToVendor}</FormHelperText>
                  )}
                  {vendorError && (
                    <FormHelperText error>{vendorError}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.awardedToContact}>
                  <InputLabel>Awarded To (Contact)</InputLabel>
                  <Select
                    value={formData.awardedToContact || ''}
                    onChange={handleContactChange}
                    label="Awarded To (Contact)"
                    disabled={
                      !formData.awardedToVendor || loadingContacts || isDisabled
                    }
                    startAdornment={
                      loadingContacts ? (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      ) : null
                    }
                  >
                    {formData.awardedToContact &&
                      !contacts.some(
                        (c) => c.name === formData.awardedToContact
                      ) && (
                        <MenuItem value={formData.awardedToContact}>
                          {formData.awardedToContact} (Previously Selected)
                        </MenuItem>
                      )}
                    {contacts.map((contact) => (
                      <MenuItem key={contact.id} value={contact.name}>
                        {contact.displayName}
                      </MenuItem>
                    ))}
                    {contacts.length === 0 &&
                      !loadingContacts &&
                      formData.awardedToVendor && (
                        <MenuItem disabled value="">
                          No contacts found for this vendor
                        </MenuItem>
                      )}
                  </Select>
                  {errors.awardedToContact && (
                    <FormHelperText>{errors.awardedToContact}</FormHelperText>
                  )}
                  {contactError && (
                    <FormHelperText error>{contactError}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </>
          )}

          {isAwarded && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={isDisabled}
                  label="Award Amount"
                  type="text"
                  value={addCommaWithTwoDecimals(formData.awardAmount)}
                  onChange={(e) => {
                    const input = e.target.value.replace(/,/g, '');

                    // allow only digits & dot
                    if (!/^\d*\.?\d*$/.test(input)) return;

                    setFormData({
                      ...formData,
                      awardAmount: input, // 🔥 store RAW value
                    });
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={isDisabled}
                  label="PO#"
                  value={formData.poNumber || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, poNumber: e.target.value });
                    if (errors.poNumber)
                      setErrors((prev) => ({ ...prev, poNumber: '' }));
                  }}
                  error={!!errors.poNumber}
                  helperText={errors.poNumber}
                />
              </Grid> */}
            </>
          )}

          {/* <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
              {isAwarded ? 'Award Details' : 'Bid Result Details'}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              You can only upload files with extension -
              .pdf/.xls/.xlsx/.doc/.docx/.zip (Maximum file size: 10MB)
            </Alert>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" gutterBottom>
              Bid Result Document
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={formData.bidResultUrl ? 6 : 12}>
                <Button
                  variant="contained"
                  component="label"
                  color={formData.bidResultError ? 'error' : 'primary'}
                  fullWidth
                  disabled={formData.bidResultIsUploading || isDisabled}
                  startIcon={
                    formData.bidResultIsUploading ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                >
                  {formData.bidResultUrl
                    ? 'Replace Bid Result File'
                    : 'Upload Bid Result File'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                    onChange={(e) => {
                      if (e.target.files?.[0])
                        processFileUpload('bidResult', e.target.files[0]);
                    }}
                  />
                </Button>
              </Grid>

              {formData.bidResultUrl && (
                <>
                  <Grid item xs={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      component="a"
                      href={formData.bidResultUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </Button>
                  </Grid>
                  <Grid item xs={3}>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => handleRemoveFile('bidResult')}
                      disabled={isDisabled}
                    >
                      Remove
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                {formData.bidResult && (
                  <Typography
                    variant="caption"
                    display="block"
                    color={formData.bidResultError ? 'error' : 'textSecondary'}
                  >
                    {formData.bidResult.name}
                    {formData.bidResult.size
                      ? ` (${(formData.bidResult.size / 1024 / 1024).toFixed(
                          2
                        )} MB)`
                      : ''}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {formData.bidResultError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {formData.bidResultError}
              </Alert>
            )}
          </Grid>

          {isAwarded && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" gutterBottom>
                PO Document
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={formData.poFileUrl ? 6 : 12}>
                  <Button
                    variant="contained"
                    component="label"
                    color={formData.poFileError ? 'error' : 'primary'}
                    fullWidth
                    disabled={formData.poFileIsUploading || isDisabled}
                    startIcon={
                      formData.poFileIsUploading ? (
                        <CircularProgress size={20} />
                      ) : null
                    }
                  >
                    {formData.poFileUrl ? 'Replace PO File' : 'Upload PO File'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                      onChange={(e) => {
                        if (e.target.files?.[0])
                          processFileUpload('poFile', e.target.files[0]);
                      }}
                    />
                  </Button>
                </Grid>

                {formData.poFileUrl && (
                  <>
                    <Grid item xs={3}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        component="a"
                        href={formData.poFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => handleRemoveFile('poFile')}
                        disabled={isDisabled}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  {formData.poFile && (
                    <Typography
                      variant="caption"
                      display="block"
                      color={formData.poFileError ? 'error' : 'textSecondary'}
                    >
                      {formData.poFile.name}
                      {formData.poFile.size
                        ? ` (${(formData.poFile.size / 1024 / 1024).toFixed(
                            2
                          )} MB)`
                        : ''}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {formData.poFileError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {formData.poFileError}
                </Alert>
              )}
            </Grid>
          )} */}
        </>
      )}

      {/* DATES */}
      <Grid
        item
        xs={12}
        sm={formData.status === 'Awarded' ? 6 : 4}
        mt={showAwardDetails && !isAwarded ? 2 : 0}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Ad Date"
            disabled={isDisabled}
            value={formData.adDate}
            onChange={(newValue) => {
              setFormData({ ...formData, adDate: newValue });
            }}
            minDate={getMinDate(formData.adDate)}
            shouldDisableDate={(date) => {
              const minDate = getMinDate(formData.adDate);
              return date < minDate;
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={formData.status === 'Awarded' ? 6 : 4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            disabled={isDisabled}
            label="Opening Date"
            value={formData.openingDate}
            onChange={(newValue) => {
              setFormData({ ...formData, openingDate: newValue });
              if (errors.openingDate)
                setErrors((prev) => ({ ...prev, openingDate: '' }));
            }}
            minDateTime={getMinDate(formData.openingDate)}
            shouldDisableDate={(date) => {
              const minDate = getMinDate(formData.openingDate);
              return date < minDate;
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={formData.status === 'Awarded' ? 6 : 4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disabled={isDisabled}
            label="Effective Date"
            value={formData.effectiveDate}
            onChange={(newValue) => {
              setFormData({ ...formData, effectiveDate: newValue });
            }}
            minDate={getMinDate(formData.effectiveDate)}
            shouldDisableDate={(date) => {
              const minDate = getMinDate(formData.effectiveDate);
              return date < minDate;
            }}
            slotProps={{
              textField: {
                fullWidth: true
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      {/* --- NEW: Reason to Re-Open (required) APPEARS ABOVE Description */}
      {formData.reopenAward && formData.status === 'Open' && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            rows={6}
            label="Reason to Re-Open"
            value={formData.reopenReason || ''}
            onChange={(e) => {
              setFormData({ ...formData, reopenReason: e.target.value });
              if (errors.reopenReason && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, reopenReason: '' }));
              }
            }}
            error={Boolean(errors.reopenReason)}
            helperText={errors.reopenReason}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          disabled={isDisabled}
          fullWidth
          multiline
          rows={6}
          label="Description"
          value={formData.description || ''}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          disabled={isDisabled}
          fullWidth
          multiline
          rows={4}
          label="Admin Notes"
          value={formData.adminNotes || ''}
          onChange={(e) =>
            setFormData({ ...formData, adminNotes: e.target.value })
          }
        />
      </Grid>
    </Grid>
  );
};

export default BidDetailsForm;
